import type {
  PlatformFacet,
  PlatformFilter,
  PlatformRecommendationsRequest,
  PlatformResult,
  PlatformSearchResponse,
  PlatformSemanticQueriesRequest,
} from '@empathyco/x-adapter-platform'
import type {
  NumberRangeFacet,
  RecommendationsRequest,
  Result,
  SearchResponse,
  SemanticQueriesRequest,
  SimpleFilter,
} from '@empathyco/x-types'
import {
  facetSchema,
  platformAdapter,
  recommendationsRequestSchema,
  relatedPromptsEndpointAdapter,
  resultSchema,
  searchResponseSchema,
  semanticQueriesRequestSchema,
  simpleFilterSchema,
} from '@empathyco/x-adapter-platform'
import { getFacetConfigWithEditable } from './utils/facets.utils'

export const adapter = platformAdapter

/* Code sample about how to extend the result mapper with more fields. */

interface BrownieSearchResponse extends Omit<PlatformSearchResponse, 'catalog'> {
  catalog: Omit<PlatformSearchResponse['catalog'], 'stats'> & {
    stats: {
      priceCurrentValue: {
        min: string
        max: string
      }
    }
  }
}

searchResponseSchema.$override<BrownieSearchResponse, Partial<SearchResponse>>({
  stats: ({ catalog }) => ({
    price: {
      min: catalog.stats?.priceCurrentValue?.min
        ? Math.floor(Number.parseFloat(catalog.stats.priceCurrentValue.min))
        : 0,
      max: catalog.stats?.priceCurrentValue?.max
        ? Math.ceil(Number.parseFloat(catalog.stats.priceCurrentValue.max))
        : 100,
    },
  }),
})

declare module '@empathyco/x-adapter-platform' {
  export interface PlatformFacet {
    label: string
  }
}

facetSchema.$override<PlatformFacet, Partial<NumberRangeFacet>>({
  modelName: ({ type, facet }) =>
    getFacetConfigWithEditable(facet, type).modelName as 'NumberRangeFacet' | undefined,
  filters: {
    $path: 'values',
    $subSchema: ({ type, facet }) => getFacetConfigWithEditable(facet, type).schema,
  },
  label: 'label',
})

interface EmpathyDemoPlatformResult extends PlatformResult {
  description: string
  collection: string
  brand: string
  title: string
  colorName: string
  colorImage: string
  availability: boolean
  priceLowest30dValue: number
}

interface BrownieSimpleFilter extends PlatformFilter {
  value: string
}

declare module '@empathyco/x-types' {
  export interface Result {
    collection: string
    description: string
    brand: string
    title: string
    color: Record<string, string>
    availability: boolean
    priceLowest30dValue: number
  }
  export interface SimpleFilter {
    value: string
    colorHex: string
  }
  export interface ResultPrice {
    /** Indicates the discount percentage */
    discount: number
  }
}

resultSchema.$override<EmpathyDemoPlatformResult, Partial<Result>>({
  availability: 'availability',
  description: 'description',
  collection: 'collection',
  title: 'title',
  brand: 'brand',
  priceLowest30dValue: 'priceLowest30dValue',
  images: ({ __images }) => (Array.isArray(__images) ? __images : [__images]),
  color: {
    name: 'colorName',
    image: 'colorImage',
  },
  price: ({ __prices: rawPrices }) => {
    const value = rawPrices?.current?.value
    const originalValue = rawPrices?.previous?.value ?? rawPrices?.current.value
    const hasDiscount = originalValue > value
    const discount = hasDiscount ? Math.round(((originalValue - value) / originalValue) * 100) : 0

    return {
      value,
      originalValue,
      hasDiscount,
      discount,
    }
  },
})

// Color filters need both a label (for display) and a value (the hexadecimal color code) in the response.
simpleFilterSchema.$override<BrownieSimpleFilter, Partial<SimpleFilter>>({
  value: 'value',
})

recommendationsRequestSchema.$override<
  RecommendationsRequest,
  Partial<PlatformRecommendationsRequest>
>({
  // TODO Top clicked demo endpoint breaks if it receives the scope parameter
  extraParams: ({ extraParams: { scope, ...extraParams } = {} }) => extraParams,
})

semanticQueriesRequestSchema.$override<
  SemanticQueriesRequest,
  Partial<PlatformSemanticQueriesRequest>
>({
  extraParams: ({ extraParams }) => {
    return {
      extraParams,
      filter_ids: 'NOT_ALL_WORDS,NOT_PARTIAL',
    }
  },
})

adapter.relatedPrompts = relatedPromptsEndpointAdapter.extends({
  endpoint: 'https://api.empathy.co/relatedprompts/mymotivemarketplace?store=Stress&lang=es',
  requestMapper: ({ query }) => ({ query }),
})
